import { useCallback, useRef } from 'react';
import { Invitation, SessionState } from 'sip.js';
import { callerLabelFromSession } from './sipCallHelpers';
/**
 * Subscribes to one primary session’s state for toolbar phase / timers.
 * Stable {@link subscribeConversationPhaseFnRef} / {@link unsubscribeConversationPhaseFnRef} pair for SessionManager delegate.
 */
export function useSipConversationPhase(setConversationUi, lastOutboundDialRef) {
    const answeredAtMsBySessionRef = useRef(new Map());
    const conversationPhaseDetachRef = useRef(null);
    const unsubscribeConversationPhase = useCallback(() => {
        conversationPhaseDetachRef.current?.();
        conversationPhaseDetachRef.current = null;
        setConversationUi(null);
    }, [setConversationUi]);
    const subscribeConversationPhase = useCallback((session) => {
        unsubscribeConversationPhase();
        const sessionId = session.id;
        const direction = session instanceof Invitation ? 'inbound' : 'outbound';
        const remoteLabel = direction === 'outbound'
            ? lastOutboundDialRef.current.trim() || callerLabelFromSession(session)
            : callerLabelFromSession(session);
        const callStartedMs = Date.now();
        const pushUi = () => {
            const st = session.state;
            if (st === SessionState.Terminated) {
                conversationPhaseDetachRef.current?.();
                conversationPhaseDetachRef.current = null;
                setConversationUi(null);
                return;
            }
            setConversationUi((current) => {
                let answeredAtMs = null;
                if (st === SessionState.Established) {
                    let t = answeredAtMsBySessionRef.current.get(sessionId);
                    if (t === undefined) {
                        t = Date.now();
                        answeredAtMsBySessionRef.current.set(sessionId, t);
                    }
                    answeredAtMs = t;
                }
                const preservedOutboundPhase = direction === 'outbound'
                    ? (current?.outboundPhase ?? 'ringing')
                    : undefined;
                return {
                    direction,
                    remoteLabel,
                    sipState: st,
                    callStartedMs,
                    answeredAtMs,
                    ...(direction === 'outbound' ? { outboundPhase: preservedOutboundPhase } : {}),
                };
            });
        };
        const onPhase = () => {
            pushUi();
        };
        const detach = () => {
            session.stateChange.removeListener(onPhase);
            answeredAtMsBySessionRef.current.delete(sessionId);
        };
        conversationPhaseDetachRef.current = detach;
        session.stateChange.addListener(onPhase);
        pushUi();
    }, [unsubscribeConversationPhase, lastOutboundDialRef, setConversationUi]);
    const subscribeConversationPhaseFnRef = useRef(subscribeConversationPhase);
    subscribeConversationPhaseFnRef.current = subscribeConversationPhase;
    const unsubscribeConversationPhaseFnRef = useRef(unsubscribeConversationPhase);
    unsubscribeConversationPhaseFnRef.current = unsubscribeConversationPhase;
    return {
        subscribeConversationPhase,
        unsubscribeConversationPhase,
        subscribeConversationPhaseFnRef,
        unsubscribeConversationPhaseFnRef,
    };
}
